import * as React from 'react';
import { FC, MouseEvent, memo, useCallback } from 'react';

import { noop } from 'lodash';
import { useIntl } from 'react-intl';

import { FavoriteIcon } from 'components/favorite-icon';
import { ButtonForIconFav } from 'components/favorite-icon/styled';
import { IconLoyaltyTiersLock } from 'components/icons/loyalty-tiers-lock';
import { FavoriteLoading } from 'components/lottie-animations/favorite-loading/favorite-loading';
import { Picture } from 'components/picture';
import { LoyaltyTierKey } from 'generated/graphql-gateway';
import {
  TierRequirementTag,
  TierRequirementTagLocked,
  TierRequirementText,
  TierRequirementTextLocked,
} from 'pages/loyalty/loyalty-incentives-components/external-incentive-details/external-incentive-details.styled.default';
import { OfferLocked } from 'pages/loyalty/offer-locked/offer-locked';
import { useAuthContext } from 'state/auth';
import { useFeaturesLoyaltyTiersContent } from 'state/loyalty/hooks/use-feature-loyalty-tiers-content';

import { ILoyaltyRewardsViewProps } from '../../loyalty-rewards/types';
import { incentiveCardImageDefaultProps } from '../constants';
import {
  BackgroundImage,
  CardContainer,
  InfoContainer,
  LoadingWrapper,
  RewardText,
  WrapperButton,
} from '../incentive-card/incentive-card.styled';

import { ImageWrapper, StyledClickableContainer } from './external-incentive-card.styled';
import { parseExternalIncentiveData } from './parse-external-incentive-data';

export interface IExternalIncentiveCard {
  incentiveData: any;
  handleClick: ILoyaltyRewardsViewProps['handleClick'];
  children?: React.ReactNode;
  favorited?: boolean;
  favoriteEnabled?: boolean;
  favoriteLoading?: boolean;
  handleToggleFavorite?: (sanityId: string, title: string) => void;
  isOfferClosed: boolean;
  offerTier?: LoyaltyTierKey;
}

export const ExternalIncentiveCard: FC<IExternalIncentiveCard> = memo(
  ({
    incentiveData,
    handleClick,
    children,
    favorited = false,
    favoriteEnabled = false,
    favoriteLoading = false,
    handleToggleFavorite = noop,
    isOfferClosed,
    offerTier,
  }) => {
    const { formatMessage } = useIntl();
    const { isAuthenticated } = useAuthContext();

    const parsedIncentiveData = parseExternalIncentiveData(incentiveData, isOfferClosed);
    const { image, title, id, backgroundImage } = parsedIncentiveData;
    const { getContentForTier } = useFeaturesLoyaltyTiersContent();

    const incentiveSanityId = id;
    const incentiveName = title || '';

    const handleFavoriteClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        event.stopPropagation();
        handleToggleFavorite(id, incentiveName);
      },
      [handleToggleFavorite, id, incentiveName]
    );

    const offerTierName = React.useMemo(() => {
      let tierName: LoyaltyTierKey;

      // if there is no tier available in the offer, set Tier_1 as default
      if (!offerTier) {
        tierName = LoyaltyTierKey.TIER_1;
      } else {
        tierName = offerTier;
      }

      // if the offer has the initial tier required we don't want to show the tier name badge
      // unless the offer is locked
      if (tierName === LoyaltyTierKey.TIER_1 && !isOfferClosed) {
        return '';
      }

      return getContentForTier(tierName)?.loyaltyTiersUIConfigName?.locale;
    }, [offerTier, getContentForTier, isOfferClosed]);

    return (
      <CardContainer>
        <StyledClickableContainer
          isLocked={isAuthenticated() && isOfferClosed}
          onClick={handleClick({
            incentiveName,
            incentiveSanityId,
            isExternalOffer: true,
          })}
        >
          {image && (
            <ImageWrapper>
              {isAuthenticated() && isOfferClosed && <OfferLocked data-testid="locked-icon" />}
              {backgroundImage && <BackgroundImage alt={title || ''} image={backgroundImage} />}
              <Picture image={image.locale} alt={title || ''} {...incentiveCardImageDefaultProps} />
            </ImageWrapper>
          )}

          <InfoContainer>
            <RewardText>{title}</RewardText>
            {children}
            {isAuthenticated() && isOfferClosed && (
              <TierRequirementTagLocked>
                <IconLoyaltyTiersLock />
                <TierRequirementTextLocked>{offerTierName}</TierRequirementTextLocked>
              </TierRequirementTagLocked>
            )}
            {isAuthenticated() && !isOfferClosed && offerTierName && (
              <TierRequirementTag>
                <TierRequirementText>{offerTierName}</TierRequirementText>
              </TierRequirementTag>
            )}
          </InfoContainer>

          {favoriteEnabled && (
            <WrapperButton>
              <ButtonForIconFav
                data-testid={favorited ? 'fav-icon-selected' : 'fav-icon'}
                onClick={handleFavoriteClick}
                aria-label={formatMessage({
                  id: favorited ? 'removeRestaurantFromFavorites' : 'setRestaurantAsFavorite',
                })}
                isTransparent={true}
              >
                {favoriteLoading ? (
                  <LoadingWrapper>
                    <FavoriteLoading />
                  </LoadingWrapper>
                ) : (
                  <FavoriteIcon isFavorite={favorited} />
                )}
              </ButtonForIconFav>
            </WrapperButton>
          )}
        </StyledClickableContainer>
      </CardContainer>
    );
  }
);
